import { toast, Id, ToastContent, ToastOptions } from 'react-toastify';

// Global flag to block toasts during logout
let isLoggingOut = false;

// Store original toast functions
const originalToast = {
  error: toast.error.bind(toast),
  success: toast.success.bind(toast),
  info: toast.info.bind(toast),
  warning: toast.warning.bind(toast),
  warn: toast.warn.bind(toast),
  dismiss: toast.dismiss.bind(toast)
};

// Function to set logout state and override toast functions
export const setLogoutState = (state: boolean) => {
  isLoggingOut = state;
  
  if (state) {
    // Clear all existing toasts immediately
    originalToast.dismiss();
    
    // NUCLEAR OPTION: Override ALL possible toast function variants
    // Return a dummy Id to satisfy TypeScript
    const dummyId: Id = 'blocked-toast';
    
    toast.error = <TData = unknown>(content: ToastContent<TData>, options?: ToastOptions): Id => {
      console.log('Toast blocked during logout');
      return dummyId;
    };
    toast.success = <TData = unknown>(content: ToastContent<TData>, options?: ToastOptions): Id => {
      console.log('Toast blocked during logout');
      return dummyId;
    };
    toast.info = <TData = unknown>(content: ToastContent<TData>, options?: ToastOptions): Id => {
      console.log('Toast blocked during logout');
      return dummyId;
    };
    toast.warning = <TData = unknown>(content: ToastContent<TData>, options?: ToastOptions): Id => {
      console.log('Toast blocked during logout');
      return dummyId;
    };
    toast.warn = <TData = unknown>(content: ToastContent<TData>, options?: ToastOptions): Id => {
      console.log('Toast blocked during logout');
      return dummyId;
    };
    
    // Also override any property that might be called
    (toast as any).default = {
      error: () => dummyId,
      success: () => dummyId,
      info: () => dummyId,
      warning: () => dummyId,
      warn: () => dummyId
    };
    
    // Keep dismiss function working
    toast.dismiss = originalToast.dismiss;
    
    // Set up MORE AGGRESSIVE interval to clear any toasts that might appear
    const clearIntervalId = setInterval(() => {
      originalToast.dismiss();
      
      // Try multiple dismiss approaches
      try {
        (toast as any).dismiss();
        (toast as any).dismissAll();
        if ((window as any).toastr) {
          (window as any).toastr.clear();
        }
      } catch (e) {
        // Ignore errors
      }
      
      // Nuclear DOM option - remove all toast elements
      const toastElements = document.querySelectorAll(
        '.Toastify__toast, .toast, .toastr, [class*="toast"], [class*="notification"]'
      );
      toastElements.forEach(el => {
        try {
          el.remove();
        } catch (e) {
          // Ignore errors
        }
      });
      
      // Remove toast containers - cast to HTMLElement for style access
      const containers = document.querySelectorAll(
        '.Toastify__toast-container, .toast-container, .toastr-container'
      );
      containers.forEach(container => {
        try {
          container.innerHTML = '';
          if (container instanceof HTMLElement) {
            container.style.display = 'none';
          }
        } catch (e) {
          // Ignore errors
        }
      });
    }, 25); // Every 25ms for maximum aggression
    
    // Clean up after 3 seconds (longer period)
    setTimeout(() => {
      clearInterval(clearIntervalId);
    }, 3000);
    
  } else {
    // Restore original toast functions
    toast.error = originalToast.error;
    toast.success = originalToast.success;
    toast.info = originalToast.info;
    toast.warning = originalToast.warning;
    toast.warn = originalToast.warn;
    toast.dismiss = originalToast.dismiss;
    
    // Remove any blocking properties
    delete (toast as any).default;
  }
};

// Enhanced toast functions that respect logout state
export const safeToast = {
  error: (message: string, options?: ToastOptions): Id | undefined => {
    if (!isLoggingOut) {
      return originalToast.error(message, options);
    }
    return undefined;
  },
  success: (message: string, options?: ToastOptions): Id | undefined => {
    if (!isLoggingOut) {
      return originalToast.success(message, options);
    }
    return undefined;
  },
  info: (message: string, options?: ToastOptions): Id | undefined => {
    if (!isLoggingOut) {
      return originalToast.info(message, options);
    }
    return undefined;
  },
  warning: (message: string, options?: ToastOptions): Id | undefined => {
    if (!isLoggingOut) {
      return originalToast.warning(message, options);
    }
    return undefined;
  },
  dismiss: originalToast.dismiss
};